// frontend/src/components/homepage/TypesLive.js
//
// "סוגי הזדמנויות" - three tilt cards, each with a conic ring that spins on
// hover, a pointer-following spotlight, and an animated icon.
//
// THE TILT IS IMPERATIVE, NOT STATE. The prototype writes four custom
// properties per pointermove and lets CSS render them; a React state update
// per pointer event would re-render the card ~60 times a second to produce the
// same four numbers. The handlers write the properties straight onto the
// element, the way the prototype does, and React never re-renders for a hover.
//
// WHEN THE TILT DOES NOT RUN: on a touch pointer (`(hover: none)`, read once,
// as the prototype does) and under motion-off. Both are checked in the handler
// itself, so a preference flipped mid-hover stops the next move rather than
// the next mount; the motion-off block in typesNumbersCta.css also pins the
// card's transform to none, so a card caught mid-tilt snaps flat.
//
// REVEAL vs TILT. In the prototype both live on one element, and the reveal's
// `.in { transform: none }` outranks the tilt, which is why the prototype's
// cards never actually tilt (verified 2026-09-03). Here the reveal is on a
// wrapper and the tilt on the card, so both work. typesNumbersCta.css says the
// same at the `.hl-ty-slot` rule.

import React, { useCallback, useEffect, useRef, useState } from "react";
import SectionShell from "./SectionShell";
import useReveal from "../../motion/useReveal";
import useMotion from "../../motion/useMotion";
import "../../homepage/typesNumbersCta.css";

/** Tilt amplitude in degrees, the prototype's literals: (px-.5)*14, (.5-py)*12. */
export const TILT_RY_DEG = 14;
export const TILT_RX_DEG = 12;

export const COPY = {
  he: {
    eyebrow: "סוגי הזדמנויות",
    heading: "הרבה מעבר ללידים - הזדמנויות אמיתיות לצמיחה עסקית.",
    sub: "כל רגע בשיח שמתנהל ממש עכשיו יכול לקדם את העסק שלך",
    cards: [
      {
        // Owner round 3: was "לקוחות חמים". The section is about
        // OPPORTUNITIES, and the other two cards are already framed that way.
        title: "הזדמנויות חמות",
        body: "לקוחות שמחפשים את השירות שלך ממש עכשיו באופן אקטיבי ומוכנים לעסקה מיידית.",
      },
      {
        title: "בניית מוניטין",
        body: "אפשרות להגיב על שאלות ודיונים מקצועיים בתחומך, להפגין מומחיות ולבנות מוניטין וצמיחה אורגנית לאורך זמן.",
      },
      {
        title: "נטוורקינג ושיתופי פעולה",
        body: "שיתופי פעולה עסקיים והזדמנויות נטוורקינג שמרחיבים את הרשת והעסק שלך.",
      },
    ],
    closeBefore: "ההיכרות עם השיח בתחום מחדדת לכם את הנישה, הנרטיב והקהל, ומביאה לכם ",
    closeStrong: "יתרון תחרותי משמעותי",
    closeAfter: ".",
  },
  en: {
    eyebrow: "Opportunity types",
    heading: "Far beyond leads - real opportunities for business growth.",
    sub: "Every moment in the conversation happening right now can move your business forward",
    cards: [
      {
        title: "Hot customers",
        body: "Customers actively looking for your service right now and ready for an immediate deal.",
      },
      {
        title: "Reputation building",
        body: "Answer professional questions and discussions in your field, show expertise and build reputation and organic growth over time.",
      },
      {
        title: "Networking and partnerships",
        body: "Business partnerships and networking opportunities that expand your network and your business.",
      },
    ],
    closeBefore: "Knowing the conversation in your field sharpens your niche, your narrative and your audience, and gives you ",
    closeStrong: "a significant competitive advantage",
    closeAfter: ".",
  },
};

/** The three cards' identities: CSS colour family + icon + reveal stagger. */
const CARDS = [
  { key: "fire", num: "01", delay: "0s" },
  { key: "rise", num: "02", delay: ".12s" },
  { key: "link", num: "03", delay: ".24s" },
];

/**
 * A touch-primary pointer, read the way the prototype reads it
 * (`matchMedia('(hover: none)')`). Fails to `false` where matchMedia is
 * missing (jsdom) - a missing capability must not switch the tilt off.
 */
function isTouchPointer() {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: none)").matches === true
    );
  } catch {
    return false;
  }
}

/** The animated icon per card, aria-hidden - the h3 beside it is the label. */
function CardIcon({ kind }) {
  if (kind === "fire") {
    return (
      <div className="hl-ty-flame" aria-hidden="true">
        <i />
        <i />
      </div>
    );
  }
  if (kind === "rise") {
    return (
      <svg className="hl-ty-chart" viewBox="0 0 34 28" aria-hidden="true" focusable="false">
        <path d="M2 24 L10 16 L16 20 L24 8 L32 4" />
        <circle cx="32" cy="4" r="3" />
      </svg>
    );
  }
  return (
    <div className="hl-ty-hands" aria-hidden="true">
      <i />
      <i />
    </div>
  );
}

function TypeCard({ card, num, kind, delay }) {
  const { off } = useMotion();
  const reveal = useReveal();
  const cardRef = useRef(null);
  const innerRef = useRef(null);
  // Read once at mount, as the prototype does with its module-level TOUCH.
  const [touch] = useState(isTouchPointer);
  // The handler reads the live value; a ref keeps it from re-binding per change.
  const offRef = useRef(off);
  offRef.current = off;

  const onPointerMove = useCallback(
    (event) => {
      if (touch || offRef.current) return;
      const el = event.currentTarget;
      const inner = innerRef.current;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const px = (event.clientX - r.left) / r.width;
      const py = (event.clientY - r.top) / r.height;
      el.style.setProperty("--hl-ty-ry", `${(px - 0.5) * TILT_RY_DEG}deg`);
      el.style.setProperty("--hl-ty-rx", `${(0.5 - py) * TILT_RX_DEG}deg`);
      if (inner) {
        inner.style.setProperty("--hl-ty-mx", `${px * 100}%`);
        inner.style.setProperty("--hl-ty-my", `${py * 100}%`);
      }
    },
    [touch]
  );

  const onPointerLeave = useCallback((event) => {
    const el = event.currentTarget;
    el.style.setProperty("--hl-ty-ry", "0deg");
    el.style.setProperty("--hl-ty-rx", "0deg");
  }, []);

  // A card caught mid-tilt when the visitor stops animations goes flat now,
  // not at the next pointerleave.
  useEffect(() => {
    if (!off || !cardRef.current) return;
    cardRef.current.style.setProperty("--hl-ty-ry", "0deg");
    cardRef.current.style.setProperty("--hl-ty-rx", "0deg");
  }, [off]);

  return (
    <div
      ref={reveal.ref}
      className={reveal.className ? `hl-ty-slot ${reveal.className}` : "hl-ty-slot"}
      style={{ "--hl-d": delay }}
    >
      <article
        ref={cardRef}
        className={`hl-ty-card hl-ty-${kind}`}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
      >
        <div className="hl-ty-ring" aria-hidden="true" />
        <div className="hl-ty-in" ref={innerRef}>
          <span className="hl-ty-num" aria-hidden="true">
            {num}
          </span>
          <div className="hl-ty-ico">
            <CardIcon kind={kind} />
          </div>
          <div>
            <h3>{card.title}</h3>
            <p>{card.body}</p>
          </div>
        </div>
      </article>
    </div>
  );
}

export default function TypesLive({ isHebrew = true }) {
  const t = isHebrew ? COPY.he : COPY.en;
  const head = useReveal();
  const close = useReveal();

  return (
    <SectionShell
      id="opportunity-types"
      scene="types"
      mesh="disperse"
      meshAlpha=".25"
      className="hl-ty"
    >
      <div ref={head.ref} className={head.className ? `hl-head ${head.className}` : "hl-head"}>
        <div className="hl-eyebrow">{t.eyebrow}</div>
        <h2 className="hl-h2">{t.heading}</h2>
        <p className="hl-sub hl-ty-sub">{t.sub}</p>
      </div>

      <div className="hl-ty-grid">
        {CARDS.map((c, i) => (
          <TypeCard key={c.key} card={t.cards[i]} num={c.num} kind={c.key} delay={c.delay} />
        ))}
      </div>

      <p
        ref={close.ref}
        className={close.className ? `hl-ty-close ${close.className}` : "hl-ty-close"}
      >
        {t.closeBefore}
        <strong>{t.closeStrong}</strong>
        {t.closeAfter}
      </p>
    </SectionShell>
  );
}
